"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidationUtils = void 0;
var moment = require("moment");
var ValidationUtils = /** @class */ (function () {
    function ValidationUtils() {
    }
    /**
     * Validates that min <= value <= max
     *
     * @param value
     * @param lowerBound
     * @param upperBound
     */
    ValidationUtils.isWithin = function (value, min, max) {
        if (value < min) {
            return { isValid: false, code: 'below_min' };
        }
        if (value > max) {
            return { isValid: false, code: 'above_max' };
        }
        return { isValid: true };
    };
    /**
     * Formats a number 1234.56 as $1,235
     *
     * @param value
     */
    ValidationUtils.formatIntCurrency = function (value, currency) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };
    /**
     * Round a number down to the nearest increment, e.g.
     * (55.1, .25) => 55.00
     * (55.24, .25) => 55.00
     * (55.25, .25) => 55.25
     * (55.26, .25) => 55.25
     * (55.26, .50) => 55.00
     * (55.76, .50) => 55.50
     *
     * @param value
     * @param increment
     */
    ValidationUtils.roundDownToNearestIncrement = function (value, increment) {
        return Math.floor(value / increment) * increment;
    };
    /**
     * Derive date of birth given an age
     *
     * @param actualAgeInYears
     */
    ValidationUtils.getDOBForActualAge = function (actualAgeInYears) {
        var roundedUpToNearestQuarterYear = Math.ceil(actualAgeInYears * 4) / 4;
        var dob = moment().startOf('day').subtract(roundedUpToNearestQuarterYear, 'years').toDate();
        var ageWithDOB = ValidationUtils.getActualAgeInFractionalYears(dob);
        while (ageWithDOB > actualAgeInYears) {
            dob.setDate(dob.getDate() + 1);
            ageWithDOB = ValidationUtils.getActualAgeInFractionalYears(dob);
        }
        return dob;
    };
    /**
     * Return actual age with fractional year
     * @param dateOfBirth
     */
    ValidationUtils.getActualAgeInFractionalYears = function (dateOfBirth) {
        //ignore time of day when doing age calculation
        var dobMoment = moment(dateOfBirth.toISOString()).startOf('day');
        var nowMoment = moment().startOf('day');
        return nowMoment.diff(dobMoment, 'years', true);
    };
    /**
     * Return actual age as whole days
     * @param dateOfBirth
     */
    ValidationUtils.getActualAgeInDays = function (dateOfBirth) {
        //ignore time of day when doing age calculation
        var dobMoment = moment(dateOfBirth.toISOString()).startOf('day');
        var nowMoment = moment().startOf('day');
        return nowMoment.diff(dobMoment, 'days');
    };
    /**
     * Validates that value is not null/undefined/empty
     * @param value
     */
    ValidationUtils.exists = function (value) {
        return value !== undefined && value !== null && value !== '';
    };
    /**
     * Return boolean indicating older or same age
     * @param dateOfBirth, age
     */
    ValidationUtils.isOlderThanOrEqualToAge = function (dateOfBirth, age) {
        var dob = new Date(dateOfBirth);
        dob.setHours(0, 0, 0, 0);
        return this.getDobForAge(age) >= dob;
    };
    ValidationUtils.getDobForAge = function (age) {
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        var ageInMonths = age * 12;
        var ageDate = this.offsetDate(today, -ageInMonths, 'months');
        ageDate.setHours(0, 0, 0, 0);
        return ageDate;
    };
    ValidationUtils.offsetDate = function (date, offset, unit) {
        unit = unit ? unit.toLowerCase() : '';
        var newDate = new Date(date.getTime());
        if (unit.match(/^days?$/)) {
            newDate.setUTCDate(date.getUTCDate() + offset);
        }
        else if (unit.match(/^months?$/)) {
            newDate.setUTCMonth(date.getUTCMonth() + offset);
        }
        else if (unit.match(/^years?$/)) {
            newDate.setUTCFullYear(date.getUTCFullYear() + offset);
        }
        return newDate;
    };
    ValidationUtils.isOlderThanAge = function (dateOfBirth, age) {
        var dob = new Date(dateOfBirth);
        dob.setHours(0, 0, 0, 0);
        return this.getDobForAge(age) > dob;
    };
    ValidationUtils.isYoungerThanAge = function (dateOfBirth, age) {
        var dob = new Date(dateOfBirth);
        dob.setHours(0, 0, 0, 0);
        return this.getDobForAge(age) < dob;
    };
    ValidationUtils.futureDate = function (value) {
        if (!(value instanceof Date)) {
            return { isValid: false, message: 'Input date must be a valid date' };
        }
        var today = new Date();
        today.setHours(23, 59, 59, 999);
        if (value < today) {
            return {
                isValid: false,
                message: "Date must be in the future"
            };
        }
        return { isValid: true };
    };
    ValidationUtils.todayOrLater = function (value) {
        if (!(value instanceof Date)) {
            return { isValid: false, message: 'Input date must be a valid date' };
        }
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        if (value < today) {
            return {
                isValid: false,
                message: 'Date must be today or later.'
            };
        }
        return { isValid: true };
    };
    ValidationUtils.todayOrEarlier = function (value) {
        if (!(value instanceof Date)) {
            return { isValid: false, message: 'Input date must be a valid date' };
        }
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        if (value > today) {
            return {
                isValid: false,
                message: 'Date must be today or earlier.'
            };
        }
        return { isValid: true };
    };
    ValidationUtils.endDateIsGreaterThanStart = function (endDate, startDate) {
        endDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        if (endDate <= startDate) {
            return {
                isValid: false,
                message: "End date must be after than start date"
            };
        }
        return { isValid: true };
    };
    ValidationUtils.getGenericProductSpecError = function () {
        return {
            isValid: false,
            message: 'Unable to validate. Missing Product Specification.'
        };
    };
    return ValidationUtils;
}());
exports.ValidationUtils = ValidationUtils;
